<template>
  <div class="studio-page">
    <div class="container text-center" v-if="isLoading">
      <HMSpinner class="mt-4" />
    </div>
    <div class="container">
      <div class="row">
        <div class="col-12">
          <NuxtLink
            class="undecorated-router-link mb-3"
            :to="{ name: 'studio-shop' }"
          >
            <i class="fal fa-chevron-left me-2"></i>
            {{ $t('STUDIO.LINKS.BACK_TO_SHOP') }}
          </NuxtLink>
          <h1 class="mt-4">{{ $t('STUDIO.TITLE.PURCHASE_A_GIFT') }}</h1>
        </div>
        <div class="col-12">
          <hr />
        </div>
      </div>
      <div class="row" v-if="!isLoading">
        <div class="col-12 products-list">
          <div class="row">
            <div
              class="col-12 col-md-9 d-flex justify-content-between mb-2 mb-md-0"
            >
              <div class="d-flex flex-wrap">
                <NuxtLink
                  v-for="tag in productTags"
                  class="undecorated-router-link"
                  :key="tag.id"
                  :to="{ query: { tag: encodeURIComponent(tag.name) } }"
                  replace
                >
                  <HMProductTag class="mt-2 me-2" :selected="tag.selected">
                    {{ tag.name }}
                  </HMProductTag>
                </NuxtLink>
              </div>
            </div>
            <div class="col-12 col-md-3">
              <HMSearch v-model="search" />
            </div>
          </div>
          <div class="row px-2" v-if="filteredProducts.length">
            <div
              class="col-6 col-md-3 mt-4 px-1 px-md-3"
              v-for="product in filteredProducts"
              :key="product.id"
            >
              <NuxtLink
                class="undecorated-router-link"
                :to="{
                  name: 'studio-product-gift',
                  params: { product_id: product.id },
                }"
              >
                <StudioProductCard
                  :isGift="true"
                  :product="product"
                  size="sm"
                />
              </NuxtLink>
            </div>
          </div>
          <div class="row" v-else>
            <div class="col-12 text-center my-5">
              <h4 class="c-dark-gray">
                {{ $t('STUDIO.TEXT.NO_PRODUCTS_MATCH_THIS_FILTER') }}
              </h4>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { useStudioStore } from '@/stores';
import StudioProductCard from '@/components/products/StudioProductCard.vue';
import { errorToMessage } from '@/utils/errors';
import RefreshData from '@/mixins/refreshData';

export default {
  name: 'StudioShop',
  components: {
    StudioProductCard,
  },
  mixins: [RefreshData],
  setup() {
    const studioStore = useStudioStore();
    definePageMeta({
      name: 'studio-gifts',

      isStudio: true,
      hideInstagramSlider: true,
    })
    return {
      RefreshData,
      studioStore
    };
  },
  computed: {
    ...mapState(useStudioStore, {
      studio: 'currentStudio',
      hasStripe: 'hasStripe',
    }),
    searchedProducts() {
      if (!this.search) return this.products;
      return this.products.filter(
        product =>
          product.product_name
            .toUpperCase()
            .indexOf(this.search.toUpperCase()) !== -1
      );
    },
    productTags() {
      if (!this.searchedProducts?.length) return [];
      const allProductsTag = {
        id: 0,
        name: this.$t('STUDIO.LABELS.ALL_PRODUCTS'),
      };
      const foundTags = {
        0: {
          ...allProductsTag,
          selected: allProductsTag.name === this.selectedProductTag,
        },
      };
      this.searchedProducts.forEach(p => {
        if (p.tags?.length) {
          p.tags.forEach(t => {
            foundTags[t.id] = {
              ...t,
              selected: t.name === this.selectedProductTag,
            };
          });
        }
      });
      return Object.values(foundTags);
    },
    selectedProductTag() {
      return decodeURIComponent(
        this.$route.query.tag || this.$t('STUDIO.LABELS.ALL_PRODUCTS')
      );
    },
    filteredProducts() {
      if (this.selectedProductTag === this.$t('STUDIO.LABELS.ALL_PRODUCTS'))
        return this.searchedProducts;
      return this.searchedProducts.filter(product =>
        product.tags.some(t => t.name === this.selectedProductTag)
      );
    },
    nonPurchasedProducts() {
      if (this.selectedProductTag === this.$t('STUDIO.LABELS.ALL_PRODUCTS'))
        return this.searchedProducts.filter(product => !product.purchased);
      return this.searchedProducts.filter(
        product =>
          !product.purchased &&
          product.tags.some(t => t.name === this.selectedProductTag)
      );
    },
    purchasedProducts() {
      return this.filteredProducts.filter(product => product.purchased);
    },
  },
  data() {
    return {
      isLoading: true,
      search: null,
      products: [],
    };
  },
  methods: {
    async loadData() {
      try {
        this.isLoading = true;
        this.products = await this.studioStore.loadGiftableProducts(this.$studioURL);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load giftable products',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';

.purchase-gift-tab {
  display: flex;
  justify-content: space-between;
  margin: 0 0 1px;
  color: var(--brand-color, $coral);
}
</style>